import CloseIcon from "@mui/icons-material/Close";
import { Box, IconButton, Stack, Tooltip, Typography } from "@mui/material";
import { format, formatDistanceToNow, isBefore, subWeeks } from "date-fns";
import { getUserColor } from "encamp-shared/src/utils/colors";
import { gql } from "generated-graphql";
import { CommentThreadFragment } from "generated-graphql/graphql";
import React, { useMemo } from "react";

import { getCommentAuthor, isCommentDeletableByUser } from "./commentPolicy";

gql(`
  fragment CommentThread on Comment {
    id
    reportId
    userId
    workflowId
    activityId
    submissionId
    comment
    isSystemMessage
    createdAt
    updatedBy
    user {
      id
      email
      person {
        first
        last
      }
    }
  }
`);

interface CommentThreadProps {
  comments: CommentThreadFragment[];
  currentUserId?: string;
  onDeleteComment?: (commentId: string) => Promise<void>;
  deleteLoading?: boolean;
  emptyStateMessage?: string;
  showDeleteButton?: boolean;
}

export function CommentThread({
  comments,
  currentUserId,
  onDeleteComment,
  deleteLoading = false,
  emptyStateMessage = "No comments yet.",
  showDeleteButton = true,
}: CommentThreadProps) {
  // sort by creation date
  const sortedComments = useMemo(
    () =>
      [...comments].sort(
        (a, b) => new Date(a.createdAt).getTime() - new Date(b.createdAt).getTime()
      ),
    [comments]
  );

  const handleDeleteComment = async (commentId: string) => {
    if (onDeleteComment) {
      await onDeleteComment(commentId);
    }
  };

  if (sortedComments.length === 0) {
    // If no empty state message is provided, don't render anything
    if (!emptyStateMessage) {
      return null;
    }

    return (
      <Box
        sx={{
          p: 2,
          bgcolor: "grey.50",
          borderRadius: 1,
          textAlign: "center",
        }}
      >
        <Typography variant="body2" color="text.secondary" sx={{ fontStyle: "italic" }}>
          {emptyStateMessage}
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={1.75}>
      {sortedComments.map((comment) => {
        const userColor = getUserColor(comment.user?.email ?? "");
        const canDelete =
          showDeleteButton && onDeleteComment && isCommentDeletableByUser(comment, currentUserId);

        return (
          <React.Fragment key={comment.id}>
            <Box
              sx={{
                display: "flex",
                alignItems: "flex-start",
                gap: 0.75,
                py: 0.25,
                "&:hover .delete-button": {
                  opacity: 1,
                },
              }}
            >
              {/* Quotation-style border */}
              <Box
                sx={{
                  width: 3,
                  minHeight: 40,
                  backgroundColor: userColor,
                  borderRadius: "1.5px",
                  flexShrink: 0,
                  alignSelf: "stretch",
                }}
              />

              {/* Delete button positioned just left of comment content */}
              <Box
                sx={{
                  width: 16,
                  mt: 0.25,
                  flexShrink: 0,
                  display: "flex",
                  justifyContent: "center",
                }}
              >
                {canDelete && (
                  <IconButton
                    className="delete-button"
                    size="small"
                    onClick={() => handleDeleteComment(comment.id)}
                    disabled={deleteLoading}
                    sx={{
                      opacity: 0,
                      transition: "opacity 0.2s",
                      width: 16,
                      height: 16,
                      "&:hover": {
                        opacity: 1,
                        backgroundColor: "rgba(211, 47, 47, 0.04)",
                      },
                    }}
                  >
                    <CloseIcon sx={{ fontSize: 12 }} />
                  </IconButton>
                )}
              </Box>

              {/* Comment content */}
              <Box
                sx={{
                  flex: 1,
                }}
              >
                <Box sx={{ display: "flex", alignItems: "center", gap: 0.5 }}>
                  <Typography variant="body2">
                    <Typography component="span" fontWeight="bold">
                      {getCommentAuthor(comment)}
                    </Typography>{" "}
                    {isOverOneWeekAgo(comment.createdAt) ? (
                      <Tooltip
                        placement="top"
                        title={format(new Date(comment.createdAt), "M/d/yyyy 'at' h:mmaaa")}
                      >
                        <Typography component="span" variant="body2" color="text.secondary">
                          {formatDistanceToNow(new Date(comment.createdAt))} ago
                        </Typography>
                      </Tooltip>
                    ) : (
                      <Typography component="span" variant="body2" color="text.secondary">
                        {format(new Date(comment.createdAt), "M/d/yyyy 'at' h:mmaaa")}
                      </Typography>
                    )}
                  </Typography>
                </Box>
                <Typography
                  variant="body2"
                  color={comment.isSystemMessage ? "text.secondary" : "text.primary"}
                  fontStyle={comment.isSystemMessage ? "italic" : "normal"}
                  sx={{ whiteSpace: "pre-wrap", mb: 0.5 }}
                >
                  {comment.comment}
                </Typography>
              </Box>
            </Box>
          </React.Fragment>
        );
      })}
    </Stack>
  );
}

const isOverOneWeekAgo = (createdAt: string) => {
  const oneWeekAgo = subWeeks(new Date(), 1);
  return isBefore(new Date(createdAt), oneWeekAgo);
};
